import { Box, createStyles, Stack } from '@mantine/core';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { AiChatHistoryController } from '../../../features/AiChatHistory';
import { useAiWorkspaceSettingEnabled } from '../../../hooks/useAiWorkspaceSettingEnabled';
import { AIChat } from '../AIChat';
import { AIAssistantProvider } from '../context';
import { useAIAssistantContext } from '../hooks';
import { AIAssistantMode } from '../types';
import { AIAssistantSidebarHeader } from './AIAssistantSidebarHeader';
import { aiSidebarSessionStore } from './sessionStore';

const useStyles = createStyles(() => ({
	container: {
		height: '100%',
	},
}));

const AIAssistantSidebarInternal = observer(() => {
	const { classes } = useStyles();
	const store = useAIAssistantContext();

	// sync the conversation id to the session storage to persist the
	// conversation in the sidebar whenever the user refreshes the page or
	// navigates away
	useEffect(
		() =>
			reaction(
				() => store.id,
				(sessionId) => {
					if (!!sessionId && sessionId !== 'new') {
						aiSidebarSessionStore.storeSessionId(sessionId);
					}
				}
			),
		[store]
	);

	return (
		<Stack className={classes.container} spacing="md">
			<Box px="md">
				<AIAssistantSidebarHeader />
			</Box>
			{store.isHistoryOpen ? <AiChatHistoryController /> : <AIChat />}
		</Stack>
	);
});

// need to wrap the top component for the sidebar in the context provider + check if AI is enabled
export function AIAssistantSidebar() {
	const enableAi = useAiWorkspaceSettingEnabled();

	if (!enableAi) {
		return null;
	}

	// load existing conversation id from session storage if it exists
	const sessionId = aiSidebarSessionStore.getSessionId();

	return (
		<AIAssistantProvider mode={AIAssistantMode.SIDEBAR} id={sessionId ?? 'new'}>
			<AIAssistantSidebarInternal />
		</AIAssistantProvider>
	);
}
